import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useEffect, useState } from 'react'
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import type {
    Life360CardMember,
    Life360CardPayload,
    Life360Office,
} from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { readToken } from '../../../../../utils/theme-colors.js'
import './life360-card.css'
import { fetchLife360Payload } from './life360-client.js'

/** How often the card re-fetches the Life360 locations so the map stays live. */
const REFRESH_MS = 60_000

const localeFor = (lang: Language): string => (lang === 'et' ? 'et-EE' : 'en-GB')

const formatTime = (iso: string, lang: Language): string => {
    const time = new Date(iso).getTime()
    if (!Number.isFinite(time) || time === 0) return '—'
    return new Date(iso).toLocaleTimeString(localeFor(lang), { hour: '2-digit', minute: '2-digit' })
}

const initials = (name: string): string =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('') || '?'

// A small initials pin avoids Leaflet's broken default marker-image paths under
// the bundler and reads better at dashboard-card size.
const PIN_SIZE = 30

const pinIcon = (member: Life360CardMember): L.DivIcon =>
    L.divIcon({
        className: 'portal-life360-card__pin',
        html: initials(member.name),
        iconSize: [PIN_SIZE, PIN_SIZE],
        iconAnchor: [PIN_SIZE / 2, PIN_SIZE / 2],
        popupAnchor: [0, -PIN_SIZE / 2],
    })

// A distinct (non-person) pin for the office so it reads as a fixed landmark.
const officeIcon = (): L.DivIcon =>
    L.divIcon({
        className: 'portal-life360-card__pin portal-life360-card__pin--office',
        html: '🏢',
        iconSize: [PIN_SIZE, PIN_SIZE],
        iconAnchor: [PIN_SIZE / 2, PIN_SIZE / 2],
        popupAnchor: [0, -PIN_SIZE / 2],
    })

// Keep the map view following the members as they move. MapContainer's
// center/bounds props only apply on mount, so a child using useMap() refits when
// positions change (keyed on the coordinates so it only runs on real movement).
const FitBounds = ({ points }: { points: [number, number][] }) => {
    const map = useMap()
    const key = points.map((p) => p.join(',')).join(';')
    useEffect(() => {
        if (points.length === 1) map.setView(points[0])
        else if (points.length > 1) map.fitBounds(points, { padding: [24, 24] })
    }, [map, key, points])
    return null
}

// Leaflet sizes itself to its container on init; when the card is resized (or
// the flex layout grows the container) it must be told to recompute, otherwise
// the map keeps its old size and leaves grey/blank area. A ResizeObserver on the
// container calls invalidateSize() on every dimension change.
const AutoResize = () => {
    const map = useMap()
    useEffect(() => {
        const observer = new ResizeObserver(() => map.invalidateSize())
        observer.observe(map.getContainer())
        return () => observer.disconnect()
    }, [map])
    return null
}

const MemberMap = ({
    members,
    office,
    lang,
}: {
    members: Life360CardMember[]
    office: Life360Office
    lang: Language
}) => {
    const officePoint: [number, number] = [office.latitude, office.longitude]
    const memberPoints = members.map((m) => [m.latitude, m.longitude] as [number, number])
    // Always keep the office in view alongside the members.
    const points = [...memberPoints, officePoint]
    const single = points.length === 1

    return (
        <MapContainer
            className="portal-life360-card__map"
            {...(single
                ? { center: points[0], zoom: 13 }
                : { bounds: points, boundsOptions: { padding: [24, 24] } })}
            scrollWheelZoom={false}
        >
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <AutoResize />
            <FitBounds points={points} />
            <Circle
                center={officePoint}
                radius={office.radiusMeters}
                pathOptions={{ color: readToken('--portal-info'), weight: 1, fillOpacity: 0.08 }}
            />
            <Marker position={officePoint} icon={officeIcon()}>
                <Popup>
                    <strong>{t.portal.dashboard.cards.life360.office(lang)}</strong>
                </Popup>
            </Marker>
            {members.map((m) => (
                <Marker key={m.id} position={[m.latitude, m.longitude]} icon={pinIcon(m)}>
                    <Popup>
                        <strong>{m.name}</strong>
                        {m.address ? <div>{m.address}</div> : null}
                    </Popup>
                </Marker>
            ))}
        </MapContainer>
    )
}

/** Pure presentational body — renders whatever payload it's given. */
const Life360CardView = ({ data, lang }: { data: Life360CardPayload; lang: Language }) => {
    const c = t.portal.dashboard.cards.life360

    if (data.status === 'not_configured') {
        return <span className="portal-card__empty">{c.notConfigured(lang)}</span>
    }

    // The wrapper is still fetching from Life360 in the browser.
    if (data.status === 'client_fetch') {
        return <span className="portal-card__subtext">{t.common.loading(lang)}</span>
    }

    if (data.status === 'error') {
        return <span className="portal-card__subtext">{c.error(lang)}</span>
    }

    if (data.members.length === 0) {
        return <span className="portal-card__empty">{c.empty(lang)}</span>
    }

    return (
        <>
            <MemberMap members={data.members} office={data.office} lang={lang} />
            <ul className="portal-life360-card__list">
                {data.members.map((m) => (
                    <li key={m.id} className="portal-life360-card__item">
                        <span className="portal-life360-card__name">{m.name}</span>
                        <span className="portal-life360-card__meta">
                            {m.battery !== null && (
                                <span className="portal-life360-card__battery">
                                    {c.battery(lang)} {m.battery}%{m.isCharging ? ' ⚡' : ''}
                                </span>
                            )}
                            {m.distanceLabel && (
                                <span className="portal-life360-card__distance">
                                    {m.distanceLabel} {c.fromOffice(lang)}
                                </span>
                            )}
                            <span className="portal-life360-card__time">
                                {c.updated(formatTime(m.lastSeen, lang))(lang)}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </>
    )
}

/**
 * Self-refreshing wrapper. The dashboard payload only carries a `client_fetch`
 * config (the server can't reach Life360 — Cloudflare blocks non-browser
 * clients), so this wrapper fetches the locations from the browser immediately
 * and then every REFRESH_MS, keeping the map live without a page reload. Other
 * statuses (not_configured / error) render as given.
 */
export const Life360Card = ({ data, lang }: { data: Life360CardPayload; lang: Language }) => {
    const [live, setLive] = useState<Life360CardPayload>(data)

    useEffect(() => {
        setLive(data)
        if (data.status !== 'client_fetch') return

        const config = { token: data.token, circleId: data.circleId }
        let cancelled = false
        const refresh = async () => {
            const next = await fetchLife360Payload(config)
            if (!cancelled) setLive(next)
        }
        void refresh()
        const id = setInterval(() => void refresh(), REFRESH_MS)
        return () => {
            cancelled = true
            clearInterval(id)
        }
    }, [data])

    return <Life360CardView data={live} lang={lang} />
}
